import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbFileText, TbId, TbCar, TbCertificate } from 'react-icons/tb';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import ActionButton from '../../../atoms/ActionButton';
import ConfirmationModal from '../../../templates/ConfirmationModal';

const BASE_URL = `${process.env.REACT_APP_PERSON_API_URL}/individual`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

interface Document {
    id?: string;
    type: string;
    number: string;
    issuingAuthority: string;
    expeditionDate?: string;
    expirationDate?: string;
    province?: string;
    isActive: boolean;
}

interface Props {
    individualId: string;
    documents: Document[];
    onDocumentsChange: (documents: Document[]) => void;
}

const IndividualFormDocuments: React.FC<Props> = ({ 
    individualId, 
    documents, 
    onDocumentsChange 
}) => {
    const isModalOpen = useSignal<boolean>(false);
    const isDeleteModalOpen = useSignal<boolean>(false);
    const isLoading = useSignal<boolean>(false);
    
    const [editingDocument, setEditingDocument] = useState<Document | null>(null);
    const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
    const [form, setForm] = useState<Document>({
        type: 'RG',
        number: '',
        issuingAuthority: '',
        expeditionDate: '',
        expirationDate: '',
        province: '',
        isActive: true
    });

    const documentTypes = [
        { value: 'RG', label: 'RG - Registro Geral' },
        { value: 'CNH', label: 'CNH - Carteira de Habilitação' },
        { value: 'PASSPORT', label: 'Passaporte' },
        { value: 'RNE', label: 'RNE - Registro Nacional de Estrangeiro' },
        { value: 'CTPS', label: 'CTPS - Carteira de Trabalho' },
        { value: 'TITULO_ELEITOR', label: 'Título de Eleitor' }
    ];

    const getDocumentIcon = (type: string) => {
        switch (type) {
            case 'RG': return <TbId className="text-blue-600" />;
            case 'CNH': return <TbCar className="text-green-600" />;
            case 'PASSPORT': return <TbCertificate className="text-purple-600" />;
            case 'RNE': return <TbCertificate className="text-orange-600" />;
            case 'CTPS': return <TbFileText className="text-indigo-600" />;
            case 'TITULO_ELEITOR': return <TbCertificate className="text-red-600" />;
            default: return <TbFileText className="text-gray-600" />;
        }
    };

    const getDocumentTypeLabel = (type: string) => {
        const typeObj = documentTypes.find(t => t.value === type);
        return typeObj ? typeObj.label : type;
    };

    const isExpired = (expirationDate: string) => {
        if (!expirationDate) return false;
        return new Date(expirationDate) < new Date();
    };

    const openModal = (document?: Document) => {
        if (document) {
            setEditingDocument(document);
            setForm(document);
        } else {
            setEditingDocument(null);
            setForm({
                type: 'RG',
                number: '',
                issuingAuthority: '',
                expeditionDate: '',
                expirationDate: '',
                province: '',
                isActive: true
            });
        }
        isModalOpen.value = true;
    };

    const closeModal = () => {
        isModalOpen.value = false;
        setEditingDocument(null);
        setForm({
            type: 'RG',
            number: '',
            issuingAuthority: '',
            expeditionDate: '',
            expirationDate: '',
            province: '',
            isActive: true
        });
    };

    const validateForm = () => {
        if (!form.number.trim()) {
            toast.error('Número do documento é obrigatório');
            return false;
        }

        if (!form.issuingAuthority.trim()) {
            toast.error('Órgão emissor é obrigatório');
            return false;
        }

        // Validate dates
        if (form.expeditionDate && form.expirationDate) {
            if (new Date(form.expeditionDate) >= new Date(form.expirationDate)) {
                toast.error('Data de expedição deve ser anterior à data de vencimento');
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        isLoading.value = true;
        try {
            const url = editingDocument 
                ? `${BASE_URL}/${individualId}/documents/${editingDocument.id}`
                : `${BASE_URL}/${individualId}/documents`;
            
            const method = editingDocument ? 'PUT' : 'POST';
            
            // ✅ CORREÇÃO: Obter JWT token do Amplify (igual aos Endereços)
            const { Auth } = await import('@aws-amplify/auth');
            const session = await Auth.currentSession();
            const token = session.getAccessToken().getJwtToken();
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`  // ✅ Token do Amplify
                },
                body: JSON.stringify({
                    type: form.type,
                    number: form.number,
                    issuing_authority: form.issuingAuthority,
                    expedition_date: form.expeditionDate || null,
                    expiration_date: form.expirationDate || null,
                    province: form.province || '',
                    is_active: form.isActive
                })
            });

            if (response.ok) {
                toast.success(editingDocument ? 'Documento atualizado!' : 'Documento cadastrado!');
                await loadDocuments();
                closeModal();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao salvar documento');
            }
        } catch (error) {
            toast.error('Erro ao salvar documento');
            console.error('Error saving document:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleDelete = async () => {
        if (!documentToDelete) return;

        isLoading.value = true;
        try {
            // ✅ CORREÇÃO: Obter JWT token do Amplify (igual aos Endereços)
            const { Auth } = await import('@aws-amplify/auth');
            const session = await Auth.currentSession();
            const token = session.getAccessToken().getJwtToken();
            
            const response = await fetch(`${BASE_URL}/${individualId}/documents/${documentToDelete}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`  // ✅ Token do Amplify
                }
            });

            if (response.ok) {
                toast.success('Documento excluído!');
                await loadDocuments();
            } else {
                toast.error('Erro ao excluir documento');
            }
        } catch (error) {
            toast.error('Erro ao excluir documento');
            console.error('Error deleting document:', error);
        } finally {
            isLoading.value = false;
            isDeleteModalOpen.value = false;
            setDocumentToDelete(null);
        }
    };

    const loadDocuments = async () => {
        console.log('📄 loadDocuments called with individualId:', individualId);
        try {
            // ✅ CORREÇÃO: Obter JWT token do Amplify (igual aos Endereços)
            const { Auth } = await import('@aws-amplify/auth');
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();  // ✅ IdToken para GET
            console.log('📄 Token obtido:', token ? 'SIM' : 'NÃO');
            
            const url = `${BASE_URL}/${individualId}/documents`;
            console.log('📄 URL da requisição:', url);
            
            const response = await fetch(url, {
                headers: {
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`  // ✅ Token do Amplify
                }
            });
            
            console.log('📄 Response status:', response.status);
            console.log('📄 Response ok:', response.ok);

            if (response.ok) {
                const data = await response.json();
                console.log('📄 Data received:', data);
                console.log('📄 Documents array:', data.documents);
                console.log('📄 Documents length:', data.documents?.length || 0);
                onDocumentsChange(data.documents || []);
            } else {
                const errorText = await response.text();
                console.error('📄 Error response:', response.status, errorText);
            }
        } catch (error) {
            console.error('📄 Error loading documents:', error);
        }
    };

    const confirmDelete = (documentId: string) => {
        setDocumentToDelete(documentId);
        isDeleteModalOpen.value = true;
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    // ✅ CORREÇÃO: Carregar documentos quando componente monta
    useEffect(() => {
        console.log('📄 useEffect triggered with individualId:', individualId);
        if (individualId) {
            loadDocuments();
        }
    }, [individualId]);

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TbFileText className="text-blue-600" />
                    Documentos
                </h3>
                <ActionButton
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2"
                >
                    <TbPlus size={16} />
                    Novo Documento
                </ActionButton>
            </div>

            {/* Lista de Documentos */}
            <div className="bg-white rounded-lg shadow">
                {documents.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <TbFileText size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>Nenhum documento cadastrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tipo</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Número</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Órgão Emissor</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Vencimento</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Status</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {documents.map((document, index) => (
                                    <tr key={document.id || index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                {getDocumentIcon(document.type)}
                                                <span className="font-medium">
                                                    {getDocumentTypeLabel(document.type)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-mono">{document.number}</td>
                                        <td className="px-4 py-3 text-sm">{document.issuingAuthority}</td>
                                        <td className="px-4 py-3 text-sm">
                                            {document.expirationDate ? (
                                                <span className={`${isExpired(document.expirationDate) ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                                                    {formatDate(document.expirationDate)}
                                                    {isExpired(document.expirationDate) && (
                                                        <span className="ml-1 text-xs">(Vencido)</span>
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">Sem vencimento</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                document.isActive 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {document.isActive ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openModal(document)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Editar"
                                                >
                                                    <TbEdit size={16} />
                                                </button>
                                                <button
                                                    onClick={() => confirmDelete(document.id!)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Excluir"
                                                >
                                                    <TbTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Cadastro/Edição */}
            {isModalOpen.value && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">
                            {editingDocument ? 'Editar Documento' : 'Novo Documento'}
                        </h3>

                        <div className="grid grid-cols-12 gap-4">
                            <SelectTextField
                                id="type"
                                label="Tipo de Documento *"
                                className="col-span-6"
                                value={form.type}
                                onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                                items={documentTypes}
                            />

                            <TextEdit
                                id="number"
                                label="Número *"
                                className="col-span-6"
                                value={form.number}
                                onChange={(e) => setForm(prev => ({ ...prev, number: e.target.value }))}
                                placeholder="Digite o número do documento"
                            />

                            <TextEdit
                                id="issuingAuthority"
                                label="Órgão Emissor *"
                                className="col-span-6"
                                value={form.issuingAuthority}
                                onChange={(e) => setForm(prev => ({ ...prev, issuingAuthority: e.target.value }))}
                                placeholder="Ex: SSP/SP, DETRAN/SP"
                            />

                            <TextEdit
                                id="province"
                                label="Estado"
                                className="col-span-6"
                                value={form.province}
                                onChange={(e) => setForm(prev => ({ ...prev, province: e.target.value }))}
                                placeholder="Ex: SP, RJ, MG"
                            />

                            <TextEdit
                                id="expeditionDate"
                                label="Data de Expedição"
                                className="col-span-6"
                                type="date"
                                value={form.expeditionDate}
                                onChange={(e) => setForm(prev => ({ ...prev, expeditionDate: e.target.value }))}
                            />

                            <TextEdit
                                id="expirationDate"
                                label="Data de Vencimento"
                                className="col-span-6"
                                type="date"
                                value={form.expirationDate}
                                onChange={(e) => setForm(prev => ({ ...prev, expirationDate: e.target.value }))}
                            />
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <ActionButton
                                onClick={closeModal}
                                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </ActionButton>
                            <ActionButton
                                onClick={handleSubmit}
                                disabled={isLoading.value}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading.value ? 'Salvando...' : 'Salvar'}
                            </ActionButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen.value}
                onCancel={() => {
                    isDeleteModalOpen.value = false;
                    setDocumentToDelete(null);
                }}
                onConfirm={handleDelete}
                title="Excluir Documento"
                id={documentToDelete || ''}
                text="Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita."
            />
        </div>
    );
};

export default IndividualFormDocuments;
